import { SvelteSet } from 'svelte/reactivity';
import { untrack } from 'svelte';
import type { FlatColumns, TableRow } from '#lib/zod/output/TabularData.js';
import type { Cell } from '#lib/server/fetching.js';
import { aggregateRows } from './utils';
import dayjs, { type Dayjs } from 'dayjs';

type SortableValue = Cell | Dayjs | string | number | undefined;
const rowIds = new WeakMap<TableRow, string>();

// ─── Public types ──────────────────────────────────────────────

export type FlatRow = {
  original: TableRow;
  id: string;
  depth: number;
  parentRows: TableRow[];
  index: number;
  canSelect: boolean;
  canExpand: boolean;
};

export type SortState =
  | {
      columnIndex: number;
      desc: boolean;
      byPeak?: boolean;
    }
  | undefined;

export type CellAccessor = (row: TableRow, columnIndex: number) => SortableValue;

export type TableStateOptions = {
  rows: () => TableRow[];
  columns: () => FlatColumns;
  filterFn: () => (r: TableRow) => boolean;
  filterLeaves: () => boolean;
  hidePeak: () => boolean;
  cellAccessor: CellAccessor;
};

function isCell(value: unknown): value is Cell {
  return typeof value === 'object' && value !== null && 'value' in value;
}

// ─── Core comparators ──────────────────────────────────────────

export function compareByValue(
  a: SortableValue,
  b: SortableValue,
  mode: 'value' | 'peak' = 'value',
): number {
  if (dayjs.isDayjs(a) && dayjs.isDayjs(b)) {
    return a.diff(b);
  }

  if (typeof a === 'string' && typeof b === 'string') {
    return a.localeCompare(b);
  }

  if (isCell(a) && isCell(b)) {
    a = a[mode];
    b = b[mode];
  }

  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }

  if (!a) return b ? -1 : 0;
  if (!b) return a ? 1 : 0;

  return 0;
}

// ─── Tree operations ───────────────────────────────────────────

function setRowId(row: TableRow, id: string): void {
  rowIds.set(row, id);
}

function getRowId(row: TableRow): string | undefined {
  return rowIds.get(row);
}

function assignStableIds(rows: TableRow[], parentPath: string = ''): void {
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const id = parentPath ? `${parentPath}.${i}` : String(i);
    setRowId(row, id);
    if ('children' in row && row.children) {
      assignStableIds(row.children, id);
    }
  }
}

function filterTree(
  rows: TableRow[],
  filterFn: (r: TableRow) => boolean,
  filterLeaves: boolean,
): TableRow[] {
  if (!filterLeaves) {
    // Only filter top-level rows (TanStack maxLeafRowFilterDepth=0 behavior)
    return rows.filter(filterFn);
  }

  // Recursively filter: keep groups if any descendant passes
  return rows
    .map((row): TableRow | undefined => {
      if ('children' in row && row.children !== undefined) {
        const filteredChildren = filterTree(row.children, filterFn, true);
        if (filteredChildren.length > 0) {
          const filteredRow = { ...row, children: filteredChildren } as TableRow;
          setRowId(filteredRow, getRowId(row) ?? '');
          return filteredRow;
        }
        // Group itself might match
        if (filterFn(row)) {
          const filteredRow = { ...row, children: filteredChildren } as TableRow;
          setRowId(filteredRow, getRowId(row) ?? '');
          return filteredRow;
        }
        return undefined;
      }
      return filterFn(row) ? row : undefined;
    })
    .filter((r): r is TableRow => r !== undefined);
}

function sortTree(rows: TableRow[], compareFn: (a: TableRow, b: TableRow) => number): TableRow[] {
  const sorted = [...rows].sort(compareFn);
  return sorted.map((row) => {
    if ((row.type === 'group' || row.type === 'lazyGroup') && row.children) {
      const sortedRow = { ...row, children: sortTree(row.children, compareFn) };
      setRowId(sortedRow, getRowId(row) ?? '');
      return sortedRow;
    }
    return row;
  });
}

function flattenTree(
  rows: TableRow[],
  expandedIds: SvelteSet<string>,
  parentPath: string,
  parentRows: TableRow[],
  result: FlatRow[],
): void {
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const id = getRowId(row) ?? (parentPath ? `${parentPath}.${i}` : String(i));
    const depth = parentRows.length;
    const canExpand = row.type === 'group' || row.type === 'lazyGroup';
    const canSelect = row.data !== undefined;

    result.push({
      original: row,
      id,
      depth,
      parentRows: [...parentRows],
      index: result.length,
      canSelect,
      canExpand,
    });

    if (canExpand && expandedIds.has(id) && 'children' in row && row.children) {
      flattenTree(row.children, expandedIds, id, [...parentRows, row], result);
    }
  }
}

function collectSelectedFromTree(
  rows: TableRow[],
  parentPath: string,
  selectedIds: SvelteSet<string>,
): TableRow[] {
  const result: TableRow[] = [];
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const id = getRowId(row) ?? (parentPath ? `${parentPath}.${i}` : String(i));
    if (row.data !== undefined && selectedIds.has(id)) {
      result.push(row);
    }
    if ('children' in row && row.children) {
      result.push(...collectSelectedFromTree(row.children, id, selectedIds));
    }
  }
  return result;
}

function collectAllFlat(rows: TableRow[], parentPath: string, parentRows: TableRow[]): FlatRow[] {
  const result: FlatRow[] = [];
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const id = getRowId(row) ?? (parentPath ? `${parentPath}.${i}` : String(i));
    const depth = parentRows.length;

    result.push({
      original: row,
      id,
      depth,
      parentRows: [...parentRows],
      index: result.length,
      canSelect: row.data !== undefined,
      canExpand: row.type === 'group' || row.type === 'lazyGroup',
    });

    if ('children' in row && row.children) {
      const children = collectAllFlat(row.children, id, [...parentRows, row]);
      for (const child of children) {
        child.index = result.length;
        result.push(child);
      }
    }
  }
  return result;
}

// ─── Selection helpers ─────────────────────────────────────────

function computeSelectionState(
  row: TableRow,
  id: string,
  selectedIds: SvelteSet<string>,
): boolean | null {
  const canSelect = row.data !== undefined;

  if (canSelect) {
    return selectedIds.has(id);
  }

  // For groups: check children
  if ('children' in row && row.children) {
    let allSelected = true;
    let someSelected = false;

    for (let i = 0; i < row.children.length; i++) {
      const childId = getRowId(row.children[i]) ?? `${id}.${i}`;
      const childState = computeSelectionState(row.children[i], childId, selectedIds);
      if (childState === true) someSelected = true;
      else if (childState === null) {
        someSelected = true;
        allSelected = false;
      } else {
        allSelected = false;
      }
    }

    if (allSelected && someSelected) return true;
    if (someSelected) return null;
    return false;
  }

  return false;
}

function collectSelectableIds(rows: TableRow[], parentPath: string): string[] {
  const result: string[] = [];
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const id = getRowId(row) ?? (parentPath ? `${parentPath}.${i}` : String(i));
    if (row.data !== undefined) result.push(id);
    if ('children' in row && row.children) {
      result.push(...collectSelectableIds(row.children, id));
    }
  }
  return result;
}

function findRowById(rows: TableRow[], id: string): TableRow | undefined {
  for (const row of rows) {
    if (getRowId(row) === id) return row;
    if ('children' in row && row.children) {
      const found = findRowById(row.children, id);
      if (found) return found;
    }
  }
  return undefined;
}

function toggleSubTreeSelection(
  rows: TableRow[],
  parentId: string,
  selectedIds: SvelteSet<string>,
  value: boolean,
): void {
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const id = getRowId(row) ?? (parentId ? `${parentId}.${i}` : String(i));
    if (row.data !== undefined) {
      if (value) selectedIds.add(id);
      else selectedIds.delete(id);
    }
    if ('children' in row && row.children) {
      toggleSubTreeSelection(row.children, id, selectedIds, value);
    }
  }
}

// ─── Main factory ──────────────────────────────────────────────

export function createTableState(options: TableStateOptions) {
  // Mutable state
  let sorting = $state<SortState>(undefined);
  const expandedIds = new SvelteSet<string>();
  const selectedIds = new SvelteSet<string>();

  // For forcing recomputation after lazy load mutations
  let rowVersion = $state(0);

  // Aggregate cache for sorting group rows (intentionally non-reactive, rebuilt in derived)
  // eslint-disable-next-line svelte/prefer-svelte-reactivity
  const aggregateCache = new Map<TableRow, TableRow>();

  // Derived chain
  const filteredRows = $derived.by(() => {
    // Touch rowVersion to recompute when rows are mutated
    void rowVersion;
    const rows = options.rows();
    assignStableIds(rows);
    const fn = options.filterFn();
    return filterTree(rows, fn, options.filterLeaves());
  });

  const sortedRows = $derived.by(() => {
    const rows = filteredRows;
    const sort = sorting;
    if (!sort) return rows;

    const { columnIndex, desc, byPeak } = sort;
    const accessor = options.cellAccessor;
    const cols = options.columns();

    // Clear and rebuild aggregate cache
    aggregateCache.clear();

    const compareFn = (a: TableRow, b: TableRow): number => {
      const [aVal, bVal] = [a, b].map((v) =>
        getCellValue(v, columnIndex, accessor, cols, aggregateCache),
      );

      const result = compareByValue(aVal, bVal, byPeak ? 'peak' : 'value');

      return desc ? -result : result;
    };

    return sortTree(rows, compareFn);
  });

  const flatRows = $derived.by(() => {
    const result: FlatRow[] = [];
    flattenTree(sortedRows, expandedIds, '', [], result);
    // Fix indices after building
    for (let i = 0; i < result.length; i++) {
      result[i].index = i;
    }
    return result;
  });

  const showPeak = $derived(
    // eslint-disable-next-line svelte/prefer-svelte-reactivity
    new Map(
      options
        .columns()
        .filter((c): c is Extract<typeof c, { key: string }> => 'key' in c)
        .map((c) => [
          c,
          !options.hidePeak() && flatRows.some((r) => r.original.data?.[c.key]?.peak !== undefined),
        ]),
    ),
  );

  // ─── Expansion ─────────────────────────────────────

  function expandAll(depth?: number | boolean) {
    expandedIds.clear();
    if (depth === false) return;

    // Walk sortedRows and expand all (or to depth)
    const maxDepth = typeof depth === 'number' ? depth : Infinity;
    _expandRecursive(sortedRows, '', 0, maxDepth);
  }

  function _expandRecursive(
    rows: TableRow[],
    parentPath: string,
    currentDepth: number,
    maxDepth: number,
  ) {
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const id = getRowId(row) ?? (parentPath ? `${parentPath}.${i}` : String(i));
      if (
        currentDepth < maxDepth &&
        (row.type === 'group' || row.type === 'lazyGroup') &&
        'children' in row &&
        row.children
      ) {
        expandedIds.add(id);
        _expandRecursive(row.children, id, currentDepth + 1, maxDepth);
      }
    }
  }

  function toggleExpand(rowId: string, value: boolean) {
    if (value) {
      expandedIds.add(rowId);
    } else {
      // Collapse this and all children
      for (const id of expandedIds) {
        if (id === rowId || id.startsWith(rowId + '.')) {
          expandedIds.delete(id);
        }
      }
    }
  }

  // ─── Selection ─────────────────────────────────────

  function toggleSelect(rowId: string, value: boolean) {
    // Find the row in the filtered tree, so writes and reads resolve against the
    // same rows — hidden rows are neither selected nor counted
    const row = findRowById(
      untrack(() => sortedRows),
      rowId,
    );
    if (!row) return;

    if (row.data !== undefined) {
      if (value) selectedIds.add(rowId);
      else selectedIds.delete(rowId);
    }

    // If it's a group, toggle all children
    if ('children' in row && row.children) {
      toggleSubTreeSelection(row.children, rowId, selectedIds, value);
    }
  }

  function selectAll(value: boolean) {
    if (value) {
      // Select only what the current filtered tree shows, so writes and reads
      // resolve against the same rows
      const ids = collectSelectableIds(
        untrack(() => sortedRows),
        '',
      );
      for (const id of ids) selectedIds.add(id);
    } else {
      selectedIds.clear();
    }
  }

  function getSelectionState(rowId: string): boolean | null {
    const row = findRowById(sortedRows, rowId);
    if (!row) return false;
    return computeSelectionState(row, rowId, selectedIds);
  }

  function getIsAllSelected(): boolean {
    const ids = collectSelectableIds(sortedRows, '');
    return ids.length > 0 && ids.every((id) => selectedIds.has(id));
  }

  function getSelectedRows(): TableRow[] {
    return collectSelectedFromTree(sortedRows, '', selectedIds);
  }

  // ─── Sorting ───────────────────────────────────────

  function toggleSort(columnIndex: number) {
    const cols = options.columns();
    const col = cols[columnIndex];
    const hasPeakOption = showPeak.get(col as any); // Data columns can have peak sorting

    if (sorting?.columnIndex === columnIndex) {
      if (sorting.desc) {
        if (hasPeakOption && !sorting.byPeak) {
          // Cycle to peak sort
          sorting = { columnIndex, desc: false, byPeak: true };
        } else {
          sorting = undefined; // Clear sort
        }
      } else {
        sorting = { ...sorting, columnIndex, desc: true };
      }
    } else {
      sorting = { columnIndex, desc: false };
    }
  }

  function setSorting(s: SortState) {
    sorting = s;
  }

  function toggleSortByName() {
    if (sorting && 'columnIndex' in sorting && sorting.columnIndex === -1) {
      sorting = sorting.desc ? undefined : { columnIndex: -1, desc: true };
    } else {
      sorting = { columnIndex: -1, desc: false };
    }
  }

  // ─── CSV / export ──────────────────────────────────

  function getAllRowsFlat(): FlatRow[] {
    return collectAllFlat(sortedRows, '', []);
  }

  // ─── Lazy loading ──────────────────────────────────

  function refreshRows() {
    rowVersion++;
  }

  return {
    get flatRows() {
      return flatRows;
    },
    get rowCount() {
      return flatRows.length;
    },
    get sorting() {
      return sorting;
    },
    get sortedRows() {
      return sortedRows;
    },
    expandedIds,
    selectedIds,

    // Expansion
    expandAll,
    toggleExpand,

    // Selection
    toggleSelect,
    selectAll,
    getSelectionState,
    getIsAllSelected,
    getSelectedRows,

    // Sorting
    toggleSort,
    toggleSortByName,
    setSorting,

    getSortingState(columnIndex: number): SortState {
      return sorting?.columnIndex === columnIndex ? sorting : undefined;
    },

    // Export
    getAllRowsFlat,

    // Lazy loading
    refreshRows,
  };
}

// ─── Helpers ───────────────────────────────────────────────────

function getCellValue(
  row: TableRow,
  columnIndex: number,
  accessor: CellAccessor,
  cols: FlatColumns,
  aggregateCache: Map<TableRow, TableRow>,
): SortableValue {
  // Name column sort
  if (columnIndex === -1) {
    return row.name;
  }

  const directValue = accessor(row, columnIndex);
  if (directValue !== undefined) return directValue;

  // For groups without data, compute aggregate
  if (row.type === 'group') {
    let aggregate = aggregateCache.get(row);
    if (!aggregate) {
      aggregate = aggregateRows(cols, row.children, 'sorting');
      aggregateCache.set(row, aggregate);
    }
    return accessor(aggregate, columnIndex);
  }

  return undefined;
}

export type TableState = ReturnType<typeof createTableState>;
